import { type Editor } from '@tiptap/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import {
  useDropdown,
  useDropdownButton,
  useDropdownMenu,
  useDropdownItem,
} from './component-provider';

export interface BlockSelectorProps {
  editor: Editor | null | undefined;
  className?: string;
}

interface BlockOption {
  name: string;
  label: string;
  isActive: (editor?: Editor | null) => boolean;
  onClick: (editor?: Editor | null) => void;
}

// Type guard for editors with isActive method
const hasIsActive = (
  editor: Editor | null | undefined,
): editor is Editor & { isActive: (name: string, attrs?: any) => boolean } => {
  return !!(
    editor &&
    'isActive' in editor &&
    typeof editor.isActive === 'function'
  );
};

const blockOptions: BlockOption[] = [
  {
    name: 'paragraph',
    label: 'Paragraph',
    isActive: (editor) => hasIsActive(editor) && editor.isActive('paragraph'),
    onClick: (editor) => editor?.chain().focus().setParagraph().run(),
  },
  {
    name: 'heading1',
    label: 'Heading 1',
    isActive: (editor) =>
      hasIsActive(editor) && editor.isActive('heading', { level: 1 }),
    onClick: (editor) =>
      editor?.chain().focus().toggleHeading({ level: 1 }).run(),
  },
  {
    name: 'heading2',
    label: 'Heading 2',
    isActive: (editor) =>
      hasIsActive(editor) && editor.isActive('heading', { level: 2 }),
    onClick: (editor) =>
      editor?.chain().focus().toggleHeading({ level: 2 }).run(),
  },
  {
    name: 'heading3',
    label: 'Heading 3',
    isActive: (editor) =>
      hasIsActive(editor) && editor.isActive('heading', { level: 3 }),
    onClick: (editor) =>
      editor?.chain().focus().toggleHeading({ level: 3 }).run(),
  },
  {
    name: 'orderedList',
    label: 'Numbered List',
    isActive: (editor) => hasIsActive(editor) && editor.isActive('orderedList'),
    onClick: (editor) => editor?.chain().focus().toggleOrderedList().run(),
  },
  {
    name: 'blockquote',
    label: 'Quote',
    isActive: (editor) => hasIsActive(editor) && editor.isActive('blockquote'),
    onClick: (editor) => editor?.chain().focus().toggleBlockquote().run(),
  },
];

export function BlockSelector({ editor, className }: BlockSelectorProps) {
  const Dropdown = useDropdown();
  const DropdownButton = useDropdownButton();
  const DropdownMenu = useDropdownMenu();
  const DropdownItem = useDropdownItem();

  // Render the selector even when editor is null so tests can assert
  // the default label. When editor is not provided, the controls are
  // rendered inert and click handlers are no-ops.

  // Determine active block with proper priority order
  let activeBlock;

  // Check blocks in priority order using guarded isActive checks.
  if (blockOptions[4].isActive(editor)) {
    activeBlock = blockOptions.find((block) => block.name === 'orderedList');
  } else if (blockOptions[5].isActive(editor)) {
    activeBlock = blockOptions.find((block) => block.name === 'blockquote');
  } else if (blockOptions[1].isActive(editor)) {
    activeBlock = blockOptions.find((block) => block.name === 'heading1');
  } else if (blockOptions[2].isActive(editor)) {
    activeBlock = blockOptions.find((block) => block.name === 'heading2');
  } else if (blockOptions[3].isActive(editor)) {
    activeBlock = blockOptions.find((block) => block.name === 'heading3');
  } else {
    activeBlock = blockOptions.find((block) => block.name === 'paragraph');
  }

  const activeLabel = activeBlock?.label || 'Paragraph';

  return (
    <Dropdown as="div" className={className}>
      <DropdownButton>
        {activeLabel}
        <ChevronDownIcon />
      </DropdownButton>

      <DropdownMenu
        transition
        className="absolute left-0 z-10 mt-2 w-64 origin-top-left rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 transition focus:outline-none data-closed:scale-95 data-closed:transform data-closed:opacity-0 data-enter:duration-100 data-enter:ease-out data-leave:duration-75 data-leave:ease-in dark:bg-gray-800 dark:ring-gray-700"
      >
        {blockOptions.map((block) => {
          return (
            <DropdownItem
              key={block.name}
              as="button"
              className="group flex w-full items-center rounded-md px-2 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => block.onClick(editor)}
            >
              {block.label}
            </DropdownItem>
          );
        })}
      </DropdownMenu>
    </Dropdown>
  );
}
